/**
 * Calls to the API's passwordless proxy (`/auth/passwordless/*`). Uses `fetch`
 * rather than `axiosInstance`, whose interceptor drops the HTTP status.
 */
import { auth0Config } from '../../config';
import { env } from '../../env';
import { StoredSession } from './tokens';

/**
 * Dev only: REACT_APP_AUTH_MOCK=true answers these calls locally (see
 * mockAuthApi.ts). `import.meta.env.DEV` is false in a build, so the mock is
 * left out of the bundle.
 */
export const isAuthMockEnabled = (): boolean =>
  import.meta.env.DEV && env.AUTH_MOCK === 'true';

export const API_URL = (env.API_URL || 'https://api.jstscifree.com').replace(
  /\/+$/,
  ''
);

export class AuthApiError extends Error {
  constructor(
    public code: string,
    public status?: number
  ) {
    super(code);
    this.name = 'AuthApiError';
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super('session_expired');
    this.name = 'SessionExpiredError';
  }
}

export interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
  scope?: string;
  token_type?: string;
}

async function post(
  path: string,
  body: Record<string, string>
): Promise<Response> {
  if (import.meta.env.DEV && isAuthMockEnabled()) {
    const { mockAuthApi } = await import('./mockAuthApi');
    return mockAuthApi(path, body);
  }
  try {
    return await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AuthApiError('network_error');
  }
}

async function toError(response: Response): Promise<AuthApiError> {
  let code = 'request_error';
  try {
    const body = await response.json();
    if (typeof body?.error === 'string') code = body.error;
  } catch {
    // no JSON body
  }
  return new AuthApiError(code, response.status);
}

const toSession = (raw: TokenResponse): StoredSession => ({
  accessToken: raw.access_token,
  idToken: raw.id_token,
  refreshToken: raw.refresh_token,
  expiresAt: Date.now() + raw.expires_in * 1000,
  source: 'passwordless',
});

export async function startPasswordless(email: string): Promise<void> {
  const response = await post('/auth/passwordless/start', { email });
  if (!response.ok) throw await toError(response);
}

export async function verifyPasswordlessOtp(
  email: string,
  otp: string
): Promise<StoredSession> {
  const response = await post('/auth/passwordless/token', { email, otp });
  if (!response.ok) throw await toError(response);
  return toSession(await response.json());
}

export async function refreshPasswordlessTokens(
  refreshToken: string
): Promise<StoredSession> {
  const response = await post('/auth/passwordless/refresh', {
    refresh_token: refreshToken,
  });
  if (!response.ok) throw await toError(response);
  const next = toSession(await response.json());
  // Auth0 may not rotate the refresh token; keep the one we have.
  next.refreshToken = next.refreshToken || refreshToken;
  return next;
}

/**
 * Revokes the refresh token at logout, so a copy of it stops working. Fire and
 * forget: `keepalive` lets the request finish after the page has moved on to
 * Auth0, and text/plain keeps it a simple request, so the browser sends no CORS
 * preflight (which a keepalive request may not get). The API reads the body as
 * JSON whatever its Content-Type.
 */
export function revokeRefreshToken(refreshToken: string): void {
  if (isAuthMockEnabled()) return;
  fetch(`${API_URL}/auth/passwordless/logout`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ refresh_token: refreshToken }),
    keepalive: true,
  }).catch(() => {
    // Nothing to do: the session is already gone from this browser.
  });
}

export function buildLogoutUrl(returnTo: string): string {
  const domain = (auth0Config.domain || '')
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');
  const query = new URLSearchParams({
    client_id: auth0Config.clientId || '',
    returnTo,
  });
  return `https://${domain}/v2/logout?${query.toString()}`;
}
